import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { Eye, EyeOff, Mail, KeyRound } from 'lucide-react'
import { validateEmail, rateLimiter, sanitizeInput } from '@/utils/inputValidation'
import { sessionManager } from '@/utils/sessionManager'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

const LoginForm = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [useMagicLink, setUseMagicLink] = useState(false)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [errors, setErrors] = useState<{ email?: string; password?: string; rate?: string; resetEmail?: string }>({})
  
  const { signIn, sendMagicLink } = useAuth()
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Clear previous errors
    setErrors({})
    
    // Enhanced client-side rate limiting
    const clientId = `login:${email || 'unknown'}`
    if (!rateLimiter.isAllowed(clientId, 5, 300000)) { // 5 attempts per 5 minutes
      const resetTime = rateLimiter.getTimeToReset(clientId, 300000)
      const resetMinutes = Math.ceil(resetTime / 60000)
      
      setErrors({ rate: `Zu viele Anmeldeversuche. Bitte versuchen Sie es in ${resetMinutes} Minuten erneut.` })
      return
    }

    // Validate inputs with enhanced sanitization
    const validationErrors: typeof errors = {}
    
    if (!validateEmail(email)) {
      validationErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    }
    
    if (!useMagicLink && !password.trim()) {
      validationErrors.password = 'Passwort ist erforderlich'
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    
    setIsLoading(true)
    
    try {
      // Check server-side rate limiting
      const { data: securityCheck, error: securityError } = await supabase.functions.invoke('auth-security', {
        body: {
          action: 'login_attempt',
          identifier: email
        }
      })

      if (securityError || !securityCheck?.allowed) {
        setErrors({ rate: securityCheck?.message || 'Sicherheitsprüfung fehlgeschlagen' })
        return
      }

      // Sanitize inputs before sending (don't sanitize passwords as they may contain special chars)
      const sanitizedEmail = sanitizeInput(email)
      
      if (useMagicLink) {
        await sendMagicLink(sanitizedEmail)
      } else {
        await signIn(sanitizedEmail, password)
        // Session management is handled in useAuth hook
      }
    } catch (error) {
      // Error handling is done in useAuth hook
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    
    console.log('🔐 Password Reset: Starting process for:', resetEmail)
    
    setErrors({})
    
    if (!validateEmail(resetEmail)) {
      console.log('❌ Password Reset: Invalid email format')
      setErrors({ resetEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein' })
      return
    }
    
    setResetLoading(true)
    
    try {
      // Check server-side rate limiting for password reset
      console.log('🛡️ Password Reset: Checking security...')
      const { data: securityCheck, error: securityError } = await supabase.functions.invoke('auth-security', {
        body: {
          action: 'password_reset_request',
          identifier: resetEmail
        }
      })

      console.log('🛡️ Security check result:', { securityCheck, securityError })

      if (securityError || !securityCheck?.allowed) {
        console.log('❌ Password Reset: Security check failed')
        toast({
          variant: "destructive",
          title: "Fehler", 
          description: securityCheck?.message || 'Sicherheitsprüfung fehlgeschlagen'
        })
        return
      }

      // Primary attempt: Use Supabase's built-in reset (reliable fallback)
      // Set a local reset guard so other tabs won't auto-login from broadcast events
      try {
        localStorage.setItem('hrz_reset_guard', JSON.stringify({ ts: Date.now(), email: resetEmail }))
        console.log('🛡️ Reset guard set')
      } catch (e) {
        console.warn('⚠️ Could not set reset guard', e)
      }
      console.log('📧 Password Reset: Trying Supabase native resetPasswordForEmail...')
      const { error: supabaseError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      })
      
      console.log('📧 Supabase native result:', { supabaseError })
      
      if (!supabaseError) {
        console.log('✅ Password Reset: Supabase native email sent successfully!')
        
        // CRITICAL: Sign out locally to avoid cross-tab auto-login in this tab
        await supabase.auth.signOut({ scope: 'local' })
        console.log('🚪 Password Reset: Local sign out to prevent auto-login in this tab')
        
        toast({
          title: "E-Mail gesendet",
          description: "Prüfen Sie Ihr E-Mail-Postfach für den Link zum Zurücksetzen des Passworts."
        })
        setShowPasswordReset(false)
        setResetEmail('')
        return
      }
      
      console.warn('⚠️ Supabase reset failed, trying custom SMTP:', supabaseError)
      
      // Fallback: Try custom Strato SMTP edge function
      console.log('📨 Password Reset: Falling back to custom Strato SMTP...')
      const { data, error } = await supabase.functions.invoke('issue-password-reset', {
        body: {
          email: resetEmail,
          redirectTo: `${window.location.origin}/reset-password`,
          sendEmail: true
        }
      })
      
      console.log('📨 Strato SMTP result:', { data, error })
      
      if (error) {
        console.error('❌ Password reset error:', error)
        toast({
          variant: "destructive",
          title: "Fehler",
          description: "E-Mail-Versand fehlgeschlagen. Bitte kontaktieren Sie den Support."
        })
      } else if (data?.success) {
        console.log('✅ Password Reset: Strato SMTP email sent successfully!')
        toast({
          title: "E-Mail gesendet",
          description: "Prüfen Sie Ihr E-Mail-Postfach für den Link zum Zurücksetzen des Passworts."
        })
        setShowPasswordReset(false)
        setResetEmail('')
      } else {
        console.error('❌ Password Reset: Unknown error state:', data)
        const errorMsg = data?.error === 'smtp_send_failed' 
          ? "SMTP-Konfiguration fehlerhaft. Admin wurde benachrichtigt."
          : data?.error || "Fehler beim Zurücksetzen des Passworts"
        toast({
          variant: "destructive",
          title: "Fehler",
          description: errorMsg
        })
      }
    } catch (error: any) {
      console.error('💥 Password reset exception:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
      })
    } finally {
      console.log('🏁 Password Reset: Process finished')
      setResetLoading(false)
    }
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {!showPasswordReset ? (
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {errors.rate && (
            <div className="p-2 sm:p-3 text-xs sm:text-sm text-destructive bg-destructive/10 rounded-md">
              {errors.rate}
            </div>
          )}
          
          <div className="space-y-1 sm:space-y-2">
            <Label htmlFor="email" className="text-sm">E-Mail-Adresse</Label>
            <Input
              id="email"
              type="email"
              placeholder="ihre@email.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-invalid={errors.email ? 'true' : 'false'}
              className="h-9 sm:h-10"
            />
            {errors.email && (
              <p className="text-xs sm:text-sm text-destructive">{errors.email}</p>
            )}
          </div>
          
          {!useMagicLink && (
            <div className="space-y-1 sm:space-y-2">
              <Label htmlFor="password" className="text-sm">Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ihr Passwort"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-invalid={errors.password ? 'true' : 'false'}
                  className="h-9 sm:h-10 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-3 w-3 sm:h-4 sm:w-4" /> : <Eye className="h-3 w-3 sm:h-4 sm:w-4" />}
                </Button>
              </div>
              {errors.password && (
                <p className="text-xs sm:text-sm text-destructive">{errors.password}</p>
              )}
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-primary to-primary-glow hover:shadow-glow transition-all duration-300 h-9 sm:h-10 text-sm"
            disabled={isLoading}
          >
            {isLoading ? 'Wird verarbeitet...' : 
             useMagicLink ? 'Magic Link senden' : 'Anmelden'}
          </Button>

          {!useMagicLink && (
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-xs sm:text-sm text-muted-foreground hover:text-primary h-auto p-0"
                onClick={() => setShowPasswordReset(true)}
              >
                Passwort vergessen?
              </Button>
            </div>
          )}

          <div className="relative">
            <Separator />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="bg-background px-2 text-xs text-muted-foreground">ODER</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-9 sm:h-10 text-sm"
            onClick={() => setUseMagicLink(!useMagicLink)}
          >
            <Mail className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            {useMagicLink ? 'Mit Passwort anmelden' : 'Magic Link verwenden'}
          </Button>
        </form>
      ) : (
        <Card className="border-0 shadow-none bg-transparent">
          <CardHeader className="px-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <KeyRound className="h-4 w-4 sm:h-5 sm:w-5" />
              Passwort zurücksetzen
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Geben Sie Ihre E-Mail-Adresse ein, um einen Link zum Zurücksetzen zu erhalten
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            <form onSubmit={handlePasswordReset} className="space-y-3 sm:space-y-4">
              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="resetEmail" className="text-sm">E-Mail-Adresse</Label>
                <Input
                  id="resetEmail"
                  type="email"
                  placeholder="ihre@email.de"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  aria-invalid={errors.resetEmail ? 'true' : 'false'}
                  className="h-9 sm:h-10"
                />
                {errors.resetEmail && (
                  <p className="text-xs sm:text-sm text-destructive">{errors.resetEmail}</p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  type="submit" 
                  className="flex-1 h-9 sm:h-10 text-sm"
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Sende...' : 'Link senden'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 sm:h-10 text-sm"
                  onClick={() => {
                    setShowPasswordReset(false)
                    setResetEmail('')
                    setErrors({})
                  }}
                >
                  Zurück
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default LoginForm